import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Play, HelpCircle, ArrowLeft, Shield, Flame, Skull,
  ChevronRight, X
} from "lucide-react";

const DIFFICULTIES = [
  { id: "easy",   label: "Easy",   icon: Shield, color: "text-emerald-500", bg: "bg-emerald-50 border-emerald-200 hover:border-emerald-400", ring: "ring-emerald-400" },
  { id: "medium", label: "Medium", icon: Flame,  color: "text-amber-500",   bg: "bg-amber-50 border-amber-200 hover:border-amber-400",     ring: "ring-amber-400" },
  { id: "hard",   label: "Hard",   icon: Skull,  color: "text-red-500",     bg: "bg-red-50 border-red-200 hover:border-red-400",           ring: "ring-red-400" },
];

export const DifficultySelector = ({ gameName, gameDescription, tutorialSteps, onStart, onBack, t }) => {
  const [selected, setSelected] = useState("medium");
  const [showTutorial, setShowTutorial] = useState(false);

  if (showTutorial) {
    return (
      <TutorialOverlay
        gameName={gameName}
        steps={tutorialSteps}
        onClose={() => setShowTutorial(false)}
        t={t}
      />
    );
  }

  return (
    <div className="text-center space-y-8 animate-in fade-in duration-300" data-testid="difficulty-selector">
      <div>
        <h2 className="text-2xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
          {gameName}
        </h2>
        <p className="text-slate-500 text-sm max-w-md mx-auto">{gameDescription}</p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
        {DIFFICULTIES.map((d) => {
          const Icon = d.icon;
          const active = selected === d.id;
          return (
            <button
              key={d.id}
              onClick={() => setSelected(d.id)}
              data-testid={`difficulty-${d.id}`}
              className={`
                relative p-4 rounded-xl border-2 transition-all duration-200
                ${d.bg}
                ${active ? `ring-2 ${d.ring} scale-105 shadow-md` : "scale-100"}
              `}
            >
              <Icon className={`w-7 h-7 mx-auto mb-2 ${d.color}`} strokeWidth={1.5} />
              <p className={`text-sm font-medium ${active ? d.color : "text-slate-600"}`}>
                {t(`games.difficulty.${d.id}`)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={() => onStart(selected)}
          className="glow-button px-8 py-3 text-base"
          data-testid="start-game-btn"
        >
          <Play className="w-5 h-5 mr-2" />
          {t("games.startGame")}
        </Button>

        <button
          onClick={() => setShowTutorial(true)}
          className="flex items-center gap-1.5 text-sm text-sky-500 hover:text-sky-700 transition-colors"
          data-testid="how-to-play-btn"
        >
          <HelpCircle className="w-4 h-4" />
          {t("games.howToPlay")}
        </button>
      </div>

      <Button
        variant="ghost"
        onClick={onBack}
        className="text-slate-400 hover:text-slate-600"
        data-testid="back-to-games-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t("games.backToGames")}
      </Button>
    </div>
  );
};

const TutorialOverlay = ({ gameName, steps, onClose, t }) => {
  const [step, setStep] = useState(0);
  const current = steps[step];

  return (
    <div className="text-center space-y-6 animate-in fade-in duration-300" data-testid="tutorial-overlay">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-sky-800">
          {t("games.howToPlay")}: {gameName}
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-tutorial-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="glass-card p-6 min-h-[200px] flex flex-col items-center justify-center">
        <div className="text-5xl mb-4">{current.visual}</div>
        <h4 className="text-lg font-medium text-sky-800 mb-2">{current.title}</h4>
        <p className="text-slate-500 text-sm max-w-md">{current.description}</p>
      </div>

      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="text-slate-500"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> {t("common.back")}
        </Button>

        <div className="flex gap-1.5">
          {steps.map((_, i) => (
            <div
              key={`dot-${i}`}
              className={`w-2 h-2 rounded-full transition-all ${i === step ? "bg-sky-500 w-6" : "bg-slate-300"}`}
            />
          ))}
        </div>

        {step < steps.length - 1 ? (
          <Button
            variant="ghost"
            onClick={() => setStep(step + 1)}
            className="text-sky-600"
          >
            {t("common.next")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={onClose}
            className="glow-button"
            data-testid="got-it-btn"
          >
            {t("games.gotIt")}
          </Button>
        )}
      </div>
    </div>
  );
};

export const GameOverScreen = ({ score, subtitle, onPlayAgain, onBack, t, children }) => (
  <div className="text-center space-y-4 animate-in fade-in duration-300" data-testid="game-over-screen">
    <p className="text-2xl text-sky-800">{t("games.gameOver")}</p>
    <p className="text-5xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>{score}</p>
    <p className="text-slate-500">{t("common.points")}</p>
    {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
    {children}
    <div className="flex gap-3 justify-center pt-2">
      <Button onClick={onPlayAgain} className="glow-button" data-testid="play-again-btn">
        {t("games.playAgain")}
      </Button>
      <Button variant="outline" onClick={onBack} data-testid="back-to-games-btn">
        {t("games.backToGames")}
      </Button>
    </div>
  </div>
);
